import { jsonrepair } from "jsonrepair";

export function smartJsonParse<T = unknown>(input: unknown): T {
  if (input === undefined) {
    return "" as T;
  }

  // parseNestedJsonStrings already guards strings with looksJsonLike + try/catch,
  // so non-JSON input (prose, "]", "<html>…") is returned untouched instead of
  // being force-fed to jsonrepair, which throws on it.
  return parseNestedJsonStrings(input) as T;
}

/**
 * Repairs and normalizes `input` via {@link smartJsonParse}, then returns a
 * pretty-printed string suitable for display in a code editor. Plain (non-JSON)
 * strings are returned as-is rather than JSON-quoted. Never throws.
 */
export function smartJsonStringify(input: unknown, space = 2): string {
  const parsed = smartJsonParse(input);

  if (typeof parsed === "string") {
    return parsed;
  }

  try {
    return JSON.stringify(parsed, null, space) ?? "";
  } catch {
    return typeof input === "string" ? input : "";
  }
}

function parseJsonish(input: string): unknown {
  let parsed: unknown = JSON.parse(jsonrepair(input));

  while (typeof parsed === "string" && looksJsonLike(parsed)) {
    parsed = JSON.parse(jsonrepair(parsed));
  }

  return parsed;
}

function parseNestedJsonStrings(value: unknown): unknown {
  if (typeof value === "string") {
    if (!looksJsonLike(value)) {
      return value;
    }

    try {
      return parseNestedJsonStrings(parseJsonish(value));
    } catch {
      return value;
    }
  }

  if (Array.isArray(value)) {
    return value.map(parseNestedJsonStrings);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        parseNestedJsonStrings(nestedValue),
      ])
    );
  }

  return value;
}

function looksJsonLike(value: string) {
  const trimmed = value.trim();

  return (
    trimmed.startsWith("{") ||
    trimmed.startsWith("[") ||
    trimmed.startsWith('"{') ||
    trimmed.startsWith('"[') ||
    trimmed.startsWith('\\"{') ||
    trimmed.startsWith('\\"[')
  );
}

export const tryParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};
